import type { DecoBuildCtx } from "./define.ts";
import { shade, rand } from "../color.ts";

// A wood-fired cooking range (stove): a stone masonry body with an iron cooktop,
// a glowing firebox at the FRONT (+z), a chimney hood + flue rising at the BACK
// (-z, meant to sit against a wall), and a pot and kettle on the hob. Placed prop
// (weight 0) — the cook's kitchen fixture; purely decorative (cooking itself is
// cook-anywhere). Geometry only — metadata lives in content/decos/range.json;
// registered in src/model/coded.ts.
const build: (ctx: DecoBuildCtx) => void = ({ box, cylinder, rod, seed }) => {
  const stone = shade(0x8a8478, (rand(seed) - 0.5) * 0.1);
  const stoneDk = shade(stone, -0.14);
  const iron = 0x3a3840;
  const ironDk = 0x27252c;
  const soot = 0x171316;
  const ember = 0xff7a2a;
  const flame = 0xffc24a;

  // --- Body ---------------------------------------------------------------
  box(0, 0.09, 0, 1.86, 0.18, 1.14, stoneDk);     // footing plinth
  box(0, 0.6, 0, 1.7, 1.02, 1.0, stone);          // main mass (top at y≈1.11)
  box(0, 1.17, 0, 1.8, 0.14, 1.08, iron);         // iron cooktop slab (top ≈1.24)
  box(0, 1.22, 0.5, 1.8, 0.05, 0.06, ironDk);     // front hob lip

  // --- Firebox (front, +z) ------------------------------------------------
  box(0, 0.5, 0.51, 1.02, 0.62, 0.1, soot);       // sooty recess
  box(0, 0.28, 0.5, 0.72, 0.18, 0.24, 0x5a3d22);  // fuel logs
  box(0, 0.34, 0.47, 0.78, 0.22, 0.18, ember);    // glowing coals
  box(0, 0.52, 0.45, 0.56, 0.4, 0.12, flame);     // flame body
  box(0, 0.76, 0.45, 0.3, 0.22, 0.1, shade(flame, 0.1)); // flame tip
  box(0, 0.64, 0.54, 1.02, 0.05, 0.04, ironDk);   // grate bars across the mouth
  box(0, 0.4, 0.54, 1.02, 0.05, 0.04, ironDk);

  // --- Chimney hood + flue (back, -z, against the wall) -------------------
  box(0, 1.72, -0.42, 1.62, 1.22, 0.2, stone);    // tall backsplash (top ≈2.33)
  box(0, 1.98, -0.02, 1.5, 0.14, 0.86, stoneDk);  // canopy jutting over the hob
  box(0, 2.62, -0.42, 0.72, 1.28, 0.5, stoneDk);  // flue rising up the wall
  box(0, 3.3, -0.42, 0.88, 0.14, 0.66, stone);    // chimney cap

  // --- Cookware on the hob (cooktop top at y≈1.24) ------------------------
  cylinder(-0.46, 1.24, 0.04, 0.3, 0.32, 0.36, 0x45413b);   // stew pot
  cylinder(-0.46, 1.58, 0.04, 0.24, 0.24, 0.06, 0x33302b);  // pot lid
  box(-0.46, 1.66, 0.04, 0.06, 0.08, 0.06, ironDk);         // lid knob
  cylinder(0.5, 1.24, 0.14, 0.22, 0.26, 0.3, 0x5b5750);     // kettle
  cylinder(0.5, 1.5, 0.14, 0.12, 0.2, 0.06, 0x4a4640);      // kettle top
  rod(0.66, 1.42, 0.14, 0.86, 1.5, 0.14, 0.035, ironDk);    // kettle spout
};

export default build;
